import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../../services/api';

const ProjectManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [projects, setProjects] = useState([]);
  const [templates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProjects, setTotalProjects] = useState(0);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCloneDialog, setOpenCloneDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Form states
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    estimatedEndDate: '',
    status: 'planning'
  });
  const [cloneData, setCloneData] = useState({
    templateProjectId: '',
    newProjectName: '',
    newProjectDescription: '',
    startDate: '',
    estimatedEndDate: ''
  });

  const projectStatuses = [
    { value: 'planning', label: 'Planning' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Wrap fetchProjects in useCallback to prevent it from changing on every render
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects', {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });
      
      setProjects(response.data.projects || []);
      setTotalProjects(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat data proyek',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]); // Add dependencies that fetchProjects relies on

  // Either remove fetchTemplates if not used or use it in the component
  // For now, let's comment it out since it's not being used
  /*
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/templates');
      setTemplates(response.data.templates || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat data template',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  */

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]); // Now fetchProjects is stable between renders

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAddDialog = () => {
    setFormData({
      name: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      estimatedEndDate: '',
      status: 'planning'
    });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
      estimatedEndDate: project.estimatedEndDate ? new Date(project.estimatedEndDate).toISOString().split('T')[0] : '',
      status: project.status || 'planning'
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (project) => {
    setSelectedProject(project);
    setOpenDeleteDialog(true);
  };

  const handleOpenCloneDialog = (project) => {
    setSelectedProject(project);
    setCloneData({
      templateProjectId: project.id,
      newProjectName: `Copy of ${project.name}`,
      newProjectDescription: project.description || '',
      startDate: new Date().toISOString().split('T')[0],
      estimatedEndDate: ''
    });
    setOpenCloneDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenCloneDialog(false);
    setSelectedProject(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloneInputChange = (e) => {
    const { name, value } = e.target;
    setCloneData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddProject = async () => {
    if (!formData.name || !formData.startDate) {
      setAlert({
        open: true,
        message: 'Nama proyek dan tanggal mulai wajib diisi',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.post('/projects', formData);
      
      setAlert({
        open: true,
        message: 'Proyek berhasil ditambahkan',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menambahkan proyek',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditProject = async () => {
    if (!formData.name || !formData.startDate) {
      setAlert({
        open: true,
        message: 'Nama proyek dan tanggal mulai wajib diisi',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.put(`/projects/${selectedProject.id}`, formData);
      
      setAlert({
        open: true,
        message: 'Proyek berhasil diperbarui',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal memperbarui proyek',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    setDialogLoading(true);
    
    try {
      // Note: This is a placeholder as the API endpoint might not exist yet
      await api.delete(`/projects/${selectedProject.id}`);
      
      setAlert({
        open: true,
        message: 'Proyek berhasil dihapus',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menghapus proyek',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloneProject = async () => {
    if (!cloneData.newProjectName || !cloneData.startDate) {
      setAlert({
        open: true,
        message: 'Nama proyek baru dan tanggal mulai wajib diisi',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.post('/admin/projects/clone', cloneData);
      
      setAlert({
        open: true,
        message: 'Proyek berhasil dikloning',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      console.error('Error cloning project:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal mengkloning proyek',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  const getStatusLabel = (statusValue) => {
    const status = projectStatuses.find(s => s.value === statusValue);
    return status ? status.label : statusValue;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning':
        return 'info';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'on_hold':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Proyek & Template
        </Typography>
        {tabValue === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Buat Proyek Baru
          </Button>
        )}
      </Box>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="project management tabs"
        >
          <Tab label="Proyek" />
          <Tab label="Template" />
        </Tabs>
      </Paper>
      
      {tabValue === 0 ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nama Proyek</TableCell>
                  <TableCell>Tanggal Mulai</TableCell>
                  <TableCell>Tanggal Selesai (Est.)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Tidak ada data proyek
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{project.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {project.description?.substring(0, 50) || '-'}
                          {project.description?.length > 50 ? '...' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {project.startDate ? new Date(project.startDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {project.estimatedEndDate ? new Date(project.estimatedEndDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(project.status)} 
                          color={getStatusColor(project.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {project.progress || 0}%
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Proyek">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(project)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone Proyek">
                          <IconButton size="small" onClick={() => handleOpenCloneDialog(project)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hapus Proyek">
                          <IconButton size="small" onClick={() => handleOpenDeleteDialog(project)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalProjects}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Template yang Tersedia
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : Object.keys(templates).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Belum ada template yang tersedia
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {Object.entries(templates).map(([type, template]) => (
                <Grid item xs={12} sm={6} md={4} key={type}>
                  <Paper sx={{ p: 2, border: '1px solid #eee' }}>
                    <Typography variant="subtitle2">
                      {type === 'kurva_s' ? 'Template Kurva S' : 
                       type === 'aes' ? 'Template AES' : 
                       type === 'rab' ? 'Template RAB' : type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.filename}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Diupload: {new Date(template.uploadedAt).toLocaleString()}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        href={template.url}
                        target="_blank"
                      >
                        Download
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload Template Baru
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload template untuk Kurva S, AES, atau RAB dalam format Excel (.xlsx) atau Microsoft Project (.mpp)
            </Typography>
            
            <Button
              variant="contained"
              component="label"
            >
              Upload Template
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.mpp"
              />
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Add/Edit Project Dialog */}
      <Dialog 
        open={openAddDialog || openEditDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {openAddDialog ? 'Buat Proyek Baru' : 'Edit Proyek'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Nama Proyek"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Deskripsi"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Tanggal Mulai"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Tanggal Selesai (Estimasi)"
              name="estimatedEndDate"
              type="date"
              value={formData.estimatedEndDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleInputChange}
              >
                {projectStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={openAddDialog ? handleAddProject : handleEditProject} 
            variant="contained" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Clone Project Dialog */}
      <Dialog 
        open={openCloneDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Clone Proyek</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Membuat salinan dari proyek: <strong>{selectedProject?.name}</strong>
          </Typography>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Nama Proyek Baru"
              name="newProjectName"
              value={cloneData.newProjectName}
              onChange={handleCloneInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Deskripsi"
              name="newProjectDescription"
              value={cloneData.newProjectDescription}
              onChange={handleCloneInputChange}
              multiline
              rows={3}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Tanggal Mulai"
              name="startDate"
              type="date"
              value={cloneData.startDate}
              onChange={handleCloneInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Tanggal Selesai (Estimasi)"
              name="estimatedEndDate"
              type="date"
              value={cloneData.estimatedEndDate}
              onChange={handleCloneInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleCloneProject} 
            variant="contained" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Clone Proyek'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Project Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hapus Proyek</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin menghapus proyek <strong>{selectedProject?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Perhatian: Menghapus proyek akan menghapus semua data terkait proyek ini, termasuk progress, dokumen, dan budget.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleDeleteProject} 
            color="error"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectManagement;
